import { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Box,
  Flex,
  Button,
  Heading,
  Text,
  Select,
  HStack,
  IconButton,
  useColorModeValue,
} from '@chakra-ui/react';
import { LayoutGrid, LayoutList, Users } from 'lucide-react';
import { Icon } from '@chakra-ui/react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useRocks } from './useRocks';
import { RocksStatsCards } from './components/RocksStatsCards';
import { RocksCardView } from './components/RocksCardView';
import { RocksTableView } from './components/RocksTableView';
import { calculateQuarter, calculateWeek } from './rocks.util';
import { Rock, User, Milestone, ActionItem, Issue } from '../types';

/**
 * Extended types for enriched rock data
 */
interface RockWithUsers extends Rock {
  owner?: User;
  collaboratorUsers?: User[];
}

interface RockWithDetails extends RockWithUsers {
  milestones?: Milestone[];
  actionItems?: ActionItem[];
}

type ViewMode = 'cards' | 'table';

/**
 * Rocks Dashboard Component
 * Main dashboard page for viewing rocks with stats, filters, and dual view modes
 * Migrated from Supabase/Tailwind to Firestore/Chakra UI
 */
export function RocksDashboard() {
  const history = useHistory();
  const { currentUser } = useAuth();
  const {
    rocks,
    users,
    loading,
    loadMilestonesForRocks,
    loadActionItemsForRocks,
    loadIssuesForRocks,
    subscribeToIssues,
    updateRock,
    deleteRock,
    achieveRock,
    dropRock,
    evaluateRock,
    loadMilestonesForRock,
    loadActionItemsForRock,
    loadIssuesForRock,
    saveRockUpdate,
    loadPreviousNotes,
    addMilestone,
    toggleMilestone,
    addActionItem,
    toggleActionItem,
    resolveIssue,
    addIssue,
    rockAiScores,
  } = useRocks(currentUser?.uid);

  const [rocksWithUsers, setRocksWithUsers] = useState<RockWithUsers[]>([]);
  const [selectedUserFilter, setSelectedUserFilter] = useState<string>(currentUser?.uid || 'all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'on-track' | 'off-track'>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const [rocksWithDetails, setRocksWithDetails] = useState<RockWithDetails[]>([]);
  const [issues, setIssues] = useState<Issue[]>([]);

  // Enrich rocks with user objects
  useEffect(() => {
    if (rocks.length > 0 && users.length > 0) {
      const usersMap = new Map(users.map((u) => [u.id, u]));
      const enriched = rocks.map((rock) => ({
        ...rock,
        owner: usersMap.get(rock.ownerId),
        collaboratorUsers:
          rock.collaborators
            ?.map((id) => usersMap.get(id))
            .filter((u): u is User => u !== undefined) || [],
      }));
      setRocksWithUsers(enriched);
    }
  }, [rocks, users]);

  // Load table data when switching to table view
  useEffect(() => {
    if (viewMode === 'table' && rocksWithUsers.length > 0) {
      loadTableData();
    }
  }, [viewMode, rocksWithUsers]);

  // Set up real-time subscription for issues when in table view
  useEffect(() => {
    if (viewMode === 'table' && rocks.length > 0) {
      const rockIds = rocks.map((r) => r.id);

      const unsubscribe = subscribeToIssues(rockIds, (updatedIssues) => {
        setIssues(updatedIssues);
        // Reload table data when issues change
        loadTableData();
      });

      return () => {
        unsubscribe();
      };
    }
  }, [viewMode, rocks]);

  const loadTableData = async () => {
    try {
      const rockIds = rocks.map((r) => r.id);

      if (rockIds.length === 0) {
        setRocksWithDetails([]);
        setIssues([]);
        return;
      }

      // Load milestones, action items, and issues in parallel
      const [milestonesMap, actionItemsMap, issuesData] = await Promise.all([
        loadMilestonesForRocks(rockIds),
        loadActionItemsForRocks(rockIds),
        loadIssuesForRocks(rockIds),
      ]);

      // Enrich rocks with milestones and action items
      const enriched = rocksWithUsers.map((rock) => ({
        ...rock,
        milestones: milestonesMap.get(rock.id) || [],
        actionItems: actionItemsMap.get(rock.id) || [],
      }));

      setRocksWithDetails(enriched);
      setIssues(issuesData);
    } catch (error) {
      console.error('Error loading table data:', error);
    }
  };

  // Filter rocks based on selected user and status
  const filteredRocksWithUsers = useMemo(() => {
    return rocksWithUsers.filter((rock) => {
      // Filter by user
      const userMatch = selectedUserFilter === 'all' || 
        rock.ownerId === selectedUserFilter ||
        rock.collaborators?.includes(selectedUserFilter);

      if (!userMatch) return false;

      // Filter by status
      if (statusFilter === 'all') return true;
      return rock.status === statusFilter;
    });
  }, [rocksWithUsers, selectedUserFilter, statusFilter]);

  const filteredRocksWithDetails = useMemo(() => {
    return rocksWithDetails.filter((rock) => {
      // Filter by user
      const userMatch = selectedUserFilter === 'all' || 
        rock.ownerId === selectedUserFilter ||
        rock.collaborators?.includes(selectedUserFilter);

      if (!userMatch) return false;

      // Filter by status
      if (statusFilter === 'all') return true;
      return rock.status === statusFilter;
    });
  }, [rocksWithDetails, selectedUserFilter, statusFilter]);

  // Calculate stats based on USER filter only, ignoring status filter
  // This ensures stats cards show correct totals even when filtering by status
  const stats = useMemo(() => {
    const userFilteredRocks = rocksWithUsers.filter((rock) => {
      if (selectedUserFilter === 'all') return true;
      return (
        rock.ownerId === selectedUserFilter ||
        rock.collaborators?.includes(selectedUserFilter)
      );
    });

    return {
      total: userFilteredRocks.length,
      onTrack: userFilteredRocks.filter((r) => r.status === 'on-track').length,
      offTrack: userFilteredRocks.filter((r) => r.status === 'off-track').length,
      avgProgress:
        userFilteredRocks.length > 0
          ? Math.round(
              userFilteredRocks.reduce((sum, r) => sum + (r.progress || 0), 0) /
                userFilteredRocks.length
            )
          : 0,
    };
  }, [rocksWithUsers, selectedUserFilter]);

  const quarter = calculateQuarter();
  const week = calculateWeek();

  // Color mode values for dark mode support
  const headingColor = useColorModeValue('gray.900', 'white');
  const subheadingColor = useColorModeValue('gray.600', 'gray.400');
  const filterLabelColor = useColorModeValue('gray.700', 'gray.300');
  const selectBg = useColorModeValue('white', 'gray.700');

  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Text color="gray.500">Loading rocks dashboard...</Text>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <Box mb={6}>
        {/* Header */}
        <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={4}>
          <Box>
            <Heading size="lg" color={headingColor}>
              {quarter} 2025 Rocks Dashboard
            </Heading>
            <Text color={subheadingColor} mt={1}>
              Week {week.current} of {week.total} - Track quarterly priorities and accountability
            </Text>
          </Box>

          {/* Controls */}
          <HStack spacing={3} flexWrap="wrap">
            {/* User Filter */}
            <HStack spacing={2}>
              <Text fontSize="sm" fontWeight="medium" color={filterLabelColor} whiteSpace="nowrap">
                Filter by User:
              </Text>
              <Select
                value={selectedUserFilter}
                onChange={(e) => setSelectedUserFilter(e.target.value)}
                size="sm"
                width="150px"
                bg={selectBg}
              >
                <option value="all">All Users</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </Select>
            </HStack>

            {/* View Mode Toggle */}
            <HStack spacing={2} borderLeft="1px" borderColor="gray.300" pl={3}>
              <IconButton
                aria-label="Card View"
                icon={<Icon as={LayoutGrid} w={5} h={5} />}
                onClick={() => setViewMode('cards')}
                colorScheme={viewMode === 'cards' ? 'blue' : 'gray'}
                variant={viewMode === 'cards' ? 'solid' : 'ghost'}
                size="sm"
              />
              <IconButton
                aria-label="Table View"
                icon={<Icon as={LayoutList} w={5} h={5} />}
                onClick={() => setViewMode('table')}
                colorScheme={viewMode === 'table' ? 'blue' : 'gray'}
                variant={viewMode === 'table' ? 'solid' : 'ghost'}
                size="sm"
              />
            </HStack>

            {/* Collaborators Button */}
            <Button
              leftIcon={<Icon as={Users} w={4} h={4} />}
              colorScheme="purple"
              size="sm"
              onClick={() => history.push('/eos/collaborators')}
            >
              Collaborators
            </Button>
          </HStack>
        </Flex>
      </Box>

      {/* Stats Cards */}
      <Box mb={6}>
        <RocksStatsCards 
          stats={stats} 
          currentFilter={statusFilter}
          onFilterChange={setStatusFilter}
        />
      </Box>

      {/* Content */}
      {viewMode === 'cards' ? (
        <RocksCardView
          rocks={filteredRocksWithUsers}
          users={users}
          currentUserId={currentUser?.uid}
          onRockClick={(rockId) => {}} // No-op, handled internally for expand
          // Action handlers
          onUpdateRock={updateRock}
          onDeleteRock={deleteRock}
          onAchieveRock={achieveRock}
          onDropRock={dropRock}
          onEvaluateRock={evaluateRock}
          onAddIssue={addIssue}
          onResolveIssue={resolveIssue} // Needs adaptation in child or here
          // Data loaders
          loadMilestonesForRock={loadMilestonesForRock}
          loadActionItemsForRock={loadActionItemsForRock}
          loadIssuesForRock={loadIssuesForRock}
          saveRockUpdate={saveRockUpdate}
          loadPreviousNotes={loadPreviousNotes}
          // Sub-item handlers
          addMilestone={addMilestone}
          toggleMilestone={toggleMilestone}
          addActionItem={addActionItem}
          toggleActionItem={toggleActionItem}
          // AI
          rockAiScores={rockAiScores}
        />
      ) : (
        <RocksTableView
          rocks={filteredRocksWithDetails}
          users={users}
          issues={issues}
          onRockClick={(rockId) => history.push(`/eos/rocks#rocks/${rockId}`)}
        />
      )}
    </Container>
  );
}
